import { DateTime } from "luxon"
// After upgrading to AG Grid v33 the styling broke using these imports. We have instead add the CSS in Webflow.
//import "ag-grid-community/styles/ag-grid.css"
//import "ag-grid-community/styles/ag-theme-alpine.css"
import {
  createGrid,
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community"
import Swal from "sweetalert2"
import * as util from "./util"
import * as utillogin from "./utillogin"
import * as utildashboard from "./utildashboard"

/* To locally debug this script via Webflow the best option I've found is to
add two scripts in Webflow:
<script src="http://localhost:3000/????.bundle.js"></script>
<script src="angry-boyd-1669aa.netlify.app/????.bundle.js" crossorigin="anonymous"></script>
The below check ensures that if localhost loads successfully the second
(production) script won't run.
Initially I tried to add the script dynamically in Webflow but that didn't
work - I needed it to run immediately but it wouldn't. */
if (typeof SharesyWebflow === "undefined") {
  util.initSentry()

  window.amplitudeOnLoad = function (instance) {
    util.amplitudeInit(instance, "Dashboard Bookings Viewed")
  }

  // Wrap everything with Revealing Module Pattern
  window.SharesyWebflow = (function () {
    var statusFilter = "All"
    // Set when user selects a status. Until they do select All if they search.
    var manuallySetStatus = false
    var isAdmin = false
    var bookingsDataCombined = []
    var bookingsDataPart1 = []
    var bookingsDataPart2 = []
    var bookingsDataPart2IsLimited = false
    var bookingsDataFilter = []
    var clickedSession
    let gridApi
    const gridOptions = {
      defaultColDef: {
        sortable: true,
        resizable: true,
      },
      animateRows: true,
      isExternalFilterPresent: isExternalFilterPresent,
      doesExternalFilterPass: doesExternalFilterPass,
      onRowClicked: onRowClicked,
      onSelectionChanged: onSelectionChanged,
      suppressExcelExport: true,
      popupParent: document.body,
      getRowId: (params) => params.data.booking_session_id,
      tooltipShowDelay: 200,
      quickFilterParser: quickFilterParser,
    }

    // Run after 400ms of no typing to avoid too many API calls
    const updateQuickFilter = util.debounce(async () => {
      let searchTerm = document.getElementById("quick-filter").value
      // Note: even if all booking data has been loaded (part1 + part2), we
      // still want to run the filter because new bookings may have arrived.
      if (searchTerm.length >= 3) {
        try {
          // Strip double quotes out of the search term
          searchTerm = searchTerm.replace(/"/g, "")
          let token = await utillogin.getClerkToken()
          let getSessionsFilter = await util.api
            .get(`get-sessions?token=${token}&search_text=${searchTerm}`, {
              retry: { limit: 4 },
            })
            .json()
          bookingsDataFilter = getSessionsFilter.sessions
          removeDupsAndUpdateGrid(
            bookingsDataCombined.concat(bookingsDataFilter)
          )
          applyQuickFilter()
        } catch (error) {
          util.logToSentry(error)
          util.logAmplitudeEvent("Unknown Error Retrieving Sessions")
        }
      }
    }, 400)

    function onRowClicked(event) {
      // Highlight the row, so the user knows which row they have selected
      const rowElement = event.event.target.closest(".ag-row")
      if (rowElement) {
        rowElement.classList.add("highlighted")
      }

      clickedSession = event.data
      utildashboard.openSessionSlideOver(
        clickedSession.session_id,
        clickedSession
      )
      // If there is only one session for this email then hide the "view all sessions" link.
      let sessionsForEmail = bookingsDataCombined.filter(
        (s) => s.email == clickedSession.email
      )
      if (sessionsForEmail.length <= 1) {
        document.getElementById("booker-all-sessions").classList.add("hide")
        document.getElementById("booker-all-sessions-dot").classList.add("hide")
      }
    }

    function onSelectionChanged(event) {
      let selectedRows = gridApi.getSelectedRows()
      let cancelSessionsSection = document.getElementById(
        "cancel-sessions-section"
      )
      if (selectedRows.length > 0) {
        cancelSessionsSection.classList.remove("hide")
        let cancelSessionsLink = document.getElementById("cancel-sessions")
        if (selectedRows.length > 1) {
          cancelSessionsLink.innerText = `Cancel ${selectedRows.length} selected sessions`
        } else {
          cancelSessionsLink.innerText = "Cancel selected session"
        }
      } else {
        cancelSessionsSection.classList.add("hide")
      }
    }

    function closeSlideOver() {
      utildashboard.closeSessionSlideOver()
      // Remove the 'highlighted' class from the row that was clicked
      const rowElement = document.querySelector(".ag-row.highlighted")
      if (rowElement) {
        rowElement.classList.remove("highlighted")
      }
    }

    function exportToCsv() {
      if (gridApi) {
        gridApi.exportDataAsCsv({
          columnKeys: [
            "booking_id",
            "session_id",
            "status_name",
            "space",
            "email",
            "first_name",
            "last_name",
            "recurring_description",
            "event_times",
            "booked_at",
            "price",
            "addon_total",
            "total_original",
            "discount",
            "total_price",
            "booker_type",
            "event_type_or_other",
            "num_people",
            "furniture_equipment",
            "other_info",
            "unpaid_invoice",
            "amount_due",
            "multi_session_invoice",
            "due_utc",
            "invoice_num",
            "venue_name",
            "phone",
            "address_line1",
            "address_line2",
            "city",
            "postal_code",
            "country",
            "payment_method_name",
            "source",
            "session_date",
            "session_month",
            "event_start_utc",
            "event_end_utc",
            "purchase_utc",
            "duration_hours",
            "original_price_per_hour",
          ],
        })
      }
    }

    /**
     * Support exact match search for double-quoted strings.
     * Copied from:
     * https://www.ag-grid.com/javascript-data-grid/filter-quick/#example-quick-filter-configuration
     */
    function quickFilterParser(quickFilter) {
      const quickFilterParts = []
      let lastSpaceIndex = -1

      const isQuote = (index) => quickFilter[index] === '"'
      const getQuickFilterPart = (lastSpaceIndex, currentIndex) => {
        const startsWithQuote = isQuote(lastSpaceIndex + 1)
        const endsWithQuote = isQuote(currentIndex - 1)
        const startIndex =
          startsWithQuote && endsWithQuote
            ? lastSpaceIndex + 2
            : lastSpaceIndex + 1
        const endIndex =
          startsWithQuote && endsWithQuote ? currentIndex - 1 : currentIndex
        return quickFilter.slice(startIndex, endIndex)
      }

      for (let i = 0; i < quickFilter.length; i++) {
        const char = quickFilter[i]
        if (char === " ") {
          if (!isQuote(lastSpaceIndex + 1) || isQuote(i - 1)) {
            quickFilterParts.push(getQuickFilterPart(lastSpaceIndex, i))
            lastSpaceIndex = i
          }
        }
      }
      if (lastSpaceIndex !== quickFilter.length - 1) {
        quickFilterParts.push(
          getQuickFilterPart(lastSpaceIndex, quickFilter.length)
        )
      }

      return quickFilterParts
    }

    async function initChecks() {
      isAdmin = await utillogin.getClerkUserIsAdmin()
      if (isAdmin) {
        utillogin.displayAdminNavItems()
      }
      await initGrid()
    }

    async function initGrid() {
      // Register all community features
      ModuleRegistry.registerModules([AllCommunityModule])
      // Mark all grids as using legacy themes
      provideGlobalGridOptions({ theme: "legacy" })

      const gridDiv = document.querySelector("#bookings-grid")
      gridOptions.columnDefs = [
        {
          headerName: "Status",
          field: "status_name",
          width: 230,
          cellStyle: (params) => {
            if (
              params.value.toLowerCase().includes("unconfirmed") &&
              DateTime.fromISO(params.data.event_start_utc) > DateTime.utc()
            ) {
              return { color: "var(--night-sky)", fontWeight: "bold" }
            }
            return null
          },
        },
        {
          headerName: "Booker",
          field: "email",
          colId: "booker",
          valueGetter: (params) =>
            (params.data.recurring_description
              ? params.data.recurring_description + " "
              : "") +
            params.data.first_name +
            " " +
            params.data.last_name +
            " " +
            params.data.email,
          cellRenderer: function (params) {
            let name = params.data.first_name + " " + params.data.last_name
            if (params.data.recurring_description) {
              name = params.data.recurring_description + " - " + name
            }
            let cellContent = `<div class="grid-multi-row-cell"><div>${name}</div><div class="grid-secondary-text">${params.data.email}</div></div>`
            return cellContent
          },
          autoHeight: true,
          width: 280,
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "Email",
          field: "email",
          hide: true,
        },
        {
          headerName: "First name",
          field: "first_name",
          hide: true,
        },
        {
          headerName: "Last name",
          field: "last_name",
          hide: true,
        },
        {
          headerName: "Recurring session name",
          field: "recurring_description",
          hide: true,
        },
        {
          headerName: "Space",
          field: isAdmin ? "space_name" : "space_display_name",
          colId: "space",
          width: isAdmin ? 320 : 240,
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "Session",
          field: "event_times",
          width: 250,
          comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
            DateTime.fromISO(nodeA.data.event_start_utc) -
            DateTime.fromISO(nodeB.data.event_start_utc),
          sortingOrder: ["desc", "asc"],
        },
        {
          headerName: "Booked at",
          field: "purchase_utc",
          colId: "booked_at",
          cellDataType: "date",
          valueFormatter: (params) => {
            return DateTime.fromISO(params.value)
              .setZone(util.TIMEZONE)
              .toFormat("ccc dd LLL yyyy HH:mm")
          },
          width: 210,
          sort: "desc",
          sortingOrder: ["desc", "asc"],
        },
        {
          headerName: "Price",
          field: "price",
          width: 120,
          cellDataType: "number",
          type: "numericColumn",
          valueFormatter: (params) => {
            return "£" + params.value
          },
          comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
            parseFloat(valueA) - parseFloat(valueB),
        },
        {
          headerName: "Addons",
          field: "addon_total",
          width: 100,
          cellDataType: "number",
          type: "numericColumn",
          valueFormatter: (params) => {
            return params.value > 0 ? "£" + params.value : ""
          },
          comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
            parseFloat(valueA) - parseFloat(valueB),
        },
        {
          headerName: "Subtotal",
          field: "total_original",
          width: 120,
          cellDataType: "number",
          type: "numericColumn",
          valueFormatter: (params) => {
            return "£" + params.value
          },
          comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
            parseFloat(valueA) - parseFloat(valueB),
        },
        {
          headerName: "Discount",
          field: "discount",
          width: 100,
          cellDataType: "number",
          type: "numericColumn",
          valueFormatter: (params) => {
            return "£" + params.value
          },
          comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
            parseFloat(valueA) - parseFloat(valueB),
        },
        {
          headerName: "Total price",
          field: "total_price",
          width: 120,
          cellDataType: "number",
          type: "numericColumn",
          valueFormatter: (params) => {
            return "£" + params.value
          },
          comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
            parseFloat(valueA) - parseFloat(valueB),
        },
        {
          headerName: "Booker type",
          field: "booker_type",
          width: 120,
        },
        {
          headerName: "Event type",
          field: "event_type_or_other",
          width: 170,
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "# people",
          field: "num_people",
          width: 170,
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "Furniture & equipment",
          field: "furniture_equipment",
          width: 170,
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "Other info",
          field: "other_info",
          width: 170,
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "Invoice unpaid?",
          field: "unpaid_invoice",
          width: 140,
          cellStyle: (params) => {
            if (params.value?.startsWith("Overdue")) {
              return { fontWeight: "bold" }
            }
          },
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "Amount owed",
          field: "amount_due",
          width: 130,
          cellDataType: "number",
          type: "numericColumn",
          valueFormatter: (params) => {
            if (params.value) {
              return "£" + params.value
            }
          },
          comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
            parseFloat(valueA ? valueA : 0) - parseFloat(valueB ? valueB : 0),
        },
        {
          headerName: "Multi-session invoice?",
          field: "multi_session_invoice",
          width: 330,
        },
        {
          headerName: "Due date",
          field: "due_utc",
          cellDataType: "date",
          valueFormatter: (params) => {
            if (params.value) {
              return DateTime.fromISO(params.value)
                .setZone(util.TIMEZONE)
                .toFormat("dd LLL yyyy")
            }
          },
          width: 140,
        },
        {
          headerName: "Invoice number",
          field: "invoice_num",
          hide: true,
        },
        {
          headerName: "Postcode",
          field: "postal_code",
          width: 120,
        },
        {
          headerName: "Venue",
          field: "venue_name",
          width: 200,
          hide: !isAdmin,
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "Source",
          field: "source",
          width: 140,
          // Don't want to e.g. search on "king" and get all "Recurring booking" rows
          getQuickFilterText: (params) => {
            return ""
          },
          tooltipValueGetter: (p) => p.value,
        },
        {
          headerName: "ID",
          field: "booking_session_id",
          width: 140,
        },
        {
          headerName: "Booking ID",
          field: "booking_id",
          hide: true,
        },
        {
          headerName: "Session ID",
          field: "session_id",
          hide: true,
        },
        {
          headerName: "Phone",
          field: "phone",
          valueGetter: (params) =>
            params.data.phone.startsWith("+") ||
            params.data.phone.startsWith("0")
              ? "'" + params.data.phone
              : params.data.phone,
          hide: true,
        },
        {
          headerName: "Address line 1",
          field: "address_line1",
          hide: true,
        },
        {
          headerName: "Address line 2",
          field: "address_line2",
          hide: true,
        },
        {
          headerName: "City",
          field: "city",
          hide: true,
        },
        {
          headerName: "Country",
          field: "country",
          hide: true,
        },
        {
          headerName: "Payment method",
          field: "payment_method_name",
          hide: true,
        },
        {
          headerName: "Session date",
          field: "event_start_utc",
          colId: "session_date",
          hide: true,
          valueGetter: (params) =>
            DateTime.fromISO(params.data.event_start_utc)
              .setZone(util.TIMEZONE)
              .toFormat("yyyy-MM-dd"),
        },
        {
          headerName: "Session month",
          field: "event_start_utc",
          colId: "session_month",
          hide: true,
          valueGetter: (params) =>
            DateTime.fromISO(params.data.event_start_utc)
              .setZone(util.TIMEZONE)
              .toFormat("yyyy-MM-01"),
        },
        {
          headerName: "Session start time",
          field: "event_start_utc",
          hide: true,
          valueGetter: (params) =>
            DateTime.fromISO(params.data.event_start_utc)
              .setZone(util.TIMEZONE)
              .toFormat("HH:mm"),
        },
        {
          headerName: "Session end time",
          field: "event_end_utc",
          hide: true,
          valueGetter: (params) =>
            DateTime.fromISO(params.data.event_start_utc)
              .setZone(util.TIMEZONE)
              .toFormat("HH:mm"),
        },
        {
          headerName: "Booked at time",
          field: "purchase_utc",
          hide: true,
          valueGetter: (params) =>
            DateTime.fromISO(params.data.event_start_utc)
              .setZone(util.TIMEZONE)
              .toFormat("yyyy-MM-dd HH:mm"),
        },
        {
          headerName: "Hours duration",
          field: "duration_hours",
          type: "numericColumn",
          hide: true,
        },
        {
          headerName: "Original price per hour",
          field: "original_price_per_hour",
          type: "numericColumn",
          hide: true,
        },
      ]

      gridApi = createGrid(gridDiv, gridOptions)

      if (util.getSearchParams().has("filter")) {
        let filterValue = util.getSearchParams().get("filter")
        util.deleteUrlParam("filter")
        filterShowAll(filterValue)
      }

      let token = await utillogin.getClerkToken()
      // Retrieving all sessions is slow, so first get upcoming sessions
      try {
        let getSessionsPart1 = await util.api
          .get(`get-sessions?token=${token}&upcoming_only=true`, {
            retry: { limit: 4 },
          })
          .json()
        bookingsDataPart1 = getSessionsPart1.sessions
        removeDupsAndUpdateGrid(bookingsDataPart1.concat(bookingsDataFilter))
        // Rerun the filter (either from the query param above, or typed by the
        // user in the mean time) against the results.
        applyQuickFilter()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Retrieving Sessions")
      }

      // It is safe to count unconfirmed and upcoming sessions now
      let unconfirmedCount = bookingsDataCombined.filter(
        (booking) =>
          !booking.confirmed &&
          !booking.cancelled &&
          DateTime.fromISO(booking.event_start_utc) > DateTime.utc()
      ).length
      document.getElementById("status-unconfirmed-sessions").innerText =
        unconfirmedCount + (unconfirmedCount == 1 ? " session" : " sessions")

      let upcomingCount = bookingsDataCombined.filter(
        (booking) =>
          !booking.cancelled &&
          DateTime.fromISO(booking.event_start_utc) > DateTime.utc()
      ).length
      document.getElementById("status-upcoming-sessions").innerText =
        upcomingCount + (upcomingCount == 1 ? " session" : " sessions")
      let searchTerm = document.getElementById("quick-filter").value

      if (unconfirmedCount > 0 && searchTerm.length == 0) {
        selectStatusFilter(document.getElementById("status-unconfirmed"), false)
      }

      let overdueCount = bookingsDataCombined.filter((booking) =>
        booking.unpaid_invoice?.startsWith("Overdue")
      ).length
      if (overdueCount > 0) {
        document.getElementById("status-overdue").classList.remove("hide")
      }
      setRowSelectionMode(bookingsDataCombined)

      // Get all other sessions
      try {
        let getSessionsPart2 = await util.api
          .get(`get-sessions?token=${token}&exclude_upcoming=true`, {
            retry: { limit: 4 },
          })
          .json()
        bookingsDataPart2 = getSessionsPart2.sessions
        removeDupsAndUpdateGrid(
          bookingsDataPart1.concat(bookingsDataFilter).concat(bookingsDataPart2)
        )
        setRowSelectionMode(bookingsDataCombined)
        applyQuickFilter()
        if (getSessionsPart2.is_limited) {
          let minBookDate = DateTime.fromISO(getSessionsPart2.min_purchase_utc)
            .setZone(util.TIMEZONE)
            .toFormat("dd LLL yyyy")
          document.getElementById(
            "session-limit-warning"
          ).innerText = `Sessions booked before ${minBookDate} won't show until you search for them`
          bookingsDataPart2IsLimited = true
          showOrHideSessionLimitWarning()
        }
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Retrieving Sessions")
      }

      // Count the sessions for the remaining status buttons
      document.getElementById("status-all-sessions").innerText =
        bookingsDataCombined.length +
        (bookingsDataCombined.length == 1 ? " session" : " sessions")

      let completedCount = bookingsDataCombined.filter(
        (booking) =>
          DateTime.fromISO(booking.event_end_utc) < DateTime.utc() &&
          !booking.cancelled
      ).length
      document.getElementById("status-completed-sessions").innerText =
        completedCount + (completedCount == 1 ? " session" : " sessions")

      let cancelledCount = bookingsDataCombined.filter(
        (booking) => booking.cancelled === true
      ).length
      document.getElementById("status-cancelled-sessions").innerText =
        cancelledCount + (cancelledCount == 1 ? " session" : " sessions")

      overdueCount = bookingsDataCombined.filter((booking) =>
        booking.unpaid_invoice?.startsWith("Overdue")
      ).length
      if (overdueCount > 0) {
        document.getElementById("status-overdue-sessions").innerText =
          overdueCount + (overdueCount == 1 ? " session" : " sessions")
        document.getElementById("status-overdue").classList.remove("hide")
      }
    }

    /* If there are cancellable sessions, then enable multi-row selection.
     */
    function setRowSelectionMode(sessions) {
      if (
        gridApi &&
        sessions.some(
          (session) =>
            session.status_code != "cancelled" &&
            (isAdmin ||
              session.source == "Recurring booking" ||
              (session.source == "Dashboard" && session.multi_session_booking))
        )
      ) {
        gridApi.setGridOption("rowSelection", {
          mode: "multiRow",
          hideDisabledCheckboxes: true,
          isRowSelectable: function (rowNode) {
            return (
              rowNode.data &&
              rowNode.data.status_code != "cancelled" &&
              (isAdmin ||
                rowNode.data.source == "Recurring booking" ||
                (rowNode.data.source == "Dashboard" &&
                  rowNode.data.multi_session_booking))
            )
          },
          selectAll: "filtered",
          enableClickSelection: false,
        })
      }
    }

    function removeDupsAndUpdateGrid(bookingsWithDups) {
      // Using a map is faster (filter was causing the front end to hang)
      bookingsDataCombined = [
        ...new Map(
          bookingsWithDups.map((item) => [item.session_id, item])
        ).values(),
      ]
      if (gridApi) {
        gridApi.setGridOption("rowData", bookingsDataCombined)
      }
    }

    function isExternalFilterPresent() {
      return statusFilter !== "All" // otherwise we are filtering
    }

    function doesExternalFilterPass(node) {
      if (node.data) {
        switch (statusFilter) {
          case "Unconfirmed":
            return (
              !node.data.confirmed &&
              !node.data.cancelled &&
              DateTime.fromISO(node.data.event_start_utc) > DateTime.utc()
            )
          case "Payment overdue":
            return node.data.unpaid_invoice?.startsWith("Overdue")
          case "Upcoming":
            return (
              DateTime.fromISO(node.data.event_start_utc) > DateTime.utc() &&
              !node.data.cancelled
            )
          case "Completed":
            return (
              DateTime.fromISO(node.data.event_end_utc) < DateTime.utc() &&
              !node.data.cancelled
            )
          case "Cancelled":
            return node.data.cancelled
          default:
            return true
        }
      }
      return true
    }

    function selectStatusFilter(button, fromUser) {
      let statusText = button.querySelector("div").innerText // eg 'Unconfirmed'
      statusFilter = statusText
      document.querySelectorAll(`div[id^="status-"]`).forEach((b) => {
        if (b.id !== button.id) {
          b.classList.remove("selected")
        }
      })
      button.classList.add("selected")
      if (gridApi) {
        gridApi.onFilterChanged()
      }
      if (fromUser) {
        util.logAmplitudeEvent("Dashboard Bookings Status Filter", {
          filter: statusText,
        })
      }
      showOrHideSessionLimitWarning()
    }

    function showOrHideSessionLimitWarning() {
      let sessionLimitWarning = document.getElementById("session-limit-warning")
      if (sessionLimitWarning && bookingsDataPart2IsLimited) {
        if (
          statusFilter === "All" ||
          statusFilter === "Completed" ||
          statusFilter === "Cancelled" ||
          statusFilter === "Payment overdue"
        ) {
          sessionLimitWarning.classList.remove("hide")
        } else {
          sessionLimitWarning.classList.add("hide")
        }
      }
    }

    function filterShowAll(filterValue) {
      document.getElementById("quick-filter").value = filterValue
      selectStatusFilter(document.getElementById("status-all"), false)
      applyQuickFilter()
      updateQuickFilter()
    }

    async function applyQuickFilter() {
      if (gridApi) {
        gridApi.setGridOption(
          "quickFilterText",
          document.getElementById("quick-filter").value
        )
      }
    }

    async function cancelSessions(sessions) {
      let swalText = null
      let allowCancellation = true
      for (let session of sessions) {
        if (session.cancelled && session.status_code != "venue_cancelled") {
          allowCancellation = false
          swalText =
            "At least one of the selected bookings has already been cancelled:" +
            ` session ${session.session_id} for ${session.first_name} ${
              session.last_name
            } at ${session.space_name} on ${DateTime.fromISO(
              session.event_start_utc
            )
              .setZone(util.TIMEZONE)
              .toFormat("ccc, dd LLL yyyy HH:mm")}.`
          break
        }
        if (DateTime.fromISO(session.cancel_by) < DateTime.now()) {
          swalText =
            "There is at least one booking past the cancellation" +
            ` deadline: session ${session.session_id} for ${
              session.first_name
            } ${session.last_name} at ${
              session.space_name
            } on ${DateTime.fromISO(session.event_start_utc)
              .setZone(util.TIMEZONE)
              .toFormat(
                "ccc, dd LLL yyyy HH:mm"
              )} needed to be cancelled by ${DateTime.fromISO(session.cancel_by)
              .setZone(util.TIMEZONE)
              .toFormat("dd LLL yyyy HH:mm")}.`
        }
      }
      if (allowCancellation) {
        let cancelType
        if (isAdmin) {
          const { value: cancelTypeReturn } = await Swal.fire({
            title: "Confirm cancellation type",
            text: swalText,
            input: "radio",
            inputOptions: {
              cancel_refund: "Cancel and refund",
              cancel_only: "Just cancel (no refund)",
            },
            confirmButtonText: "Confirm",
            confirmButtonColor: "#1aa979",
            showCancelButton: true,
            cancelButtonText: "Close",
            cancelButtonColor: "#a3a3a3",
            reverseButtons: true,
            customClass: {
              popup: "swal-custom-popup",
              title: "swal-custom-title",
              confirmButton: "swal-custom-button",
              cancelButton: "swal-custom-button",
            },
            inputValidator: (value) => {
              if (!value) {
                return "You need to choose an option"
              }
            },
          })
          cancelType = cancelTypeReturn
        } else {
          const { isConfirmed: cancelConfirmed } = await Swal.fire({
            title: "Are you sure you want to cancel and refund?",
            text: swalText,
            confirmButtonText: "Cancel and refund",
            confirmButtonColor: "#1aa979",
            showCancelButton: true,
            cancelButtonText: "Close",
            cancelButtonColor: "#a3a3a3",
            reverseButtons: true,
            customClass: {
              popup: "swal-custom-popup",
              title: "swal-custom-title",
              confirmButton: "swal-custom-button",
              cancelButton: "swal-custom-button",
            },
          })
          if (cancelConfirmed) {
            cancelType = "cancel_refund"
          }
        }
        if (cancelType) {
          let processRefund = true
          if (cancelType == "cancel_only") {
            processRefund = false
          }
          try {
            const data = await util.api
              .post("cancel-sessions-token", {
                json: {
                  token: await utillogin.getClerkToken(),
                  session_ids: sessions.map((s) => s.session_id),
                  process_refund: processRefund,
                },
                retry: { limit: 0 },
                timeout: false,
              })
              .json()
            if (data.success) {
              util.logAmplitudeEvent("Sessions Cancelled", {
                refunded: data.refunded,
              })
              if (data.refunded || !processRefund) {
                Swal.fire({
                  icon: "success",
                  title: "Sessions cancelled",
                  showConfirmButton: false,
                  customClass: {
                    popup: "swal-custom-popup",
                    title: "swal-custom-title",
                  },
                  timer: 2000,
                }).then((result) => {
                  window.location.reload()
                })
              } else {
                await Swal.fire({
                  icon: "error",
                  title: "Refund failure",
                  text:
                    "The sessions were cancelled but the following refunds failed. " +
                    data.reason,
                  confirmButtonText: "Reload page",
                  confirmButtonColor: "#d31818",
                  customClass: {
                    popup: "swal-custom-popup",
                    title: "swal-custom-title",
                    confirmButton: "swal-custom-button",
                  },
                  allowOutsideClick: false,
                })
                window.location.reload()
              }
            } else {
              await Swal.fire({
                icon: "error",
                title: "Cancellation failed",
                text:
                  "Error cancelling session - you may need to try again. " +
                  (data.reason ? data.reason : ""),
                confirmButtonText: "OK",
                confirmButtonColor: "#d31818",
                customClass: {
                  popup: "swal-custom-popup",
                  title: "swal-custom-title",
                  confirmButton: "swal-custom-button",
                },
              })
            }
          } catch (error) {
            await Swal.fire({
              icon: "error",
              title: "Cancellation failed",
              text: "Error cancelling session - you may need to try again.",
              confirmButtonText: "OK",
              confirmButtonColor: "#d31818",
              customClass: {
                popup: "swal-custom-popup",
                title: "swal-custom-title",
                confirmButton: "swal-custom-button",
              },
            })
            util.logToSentry(error)
            util.logAmplitudeEvent("Unknown Error Cancelling Sessions")
          }
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Cannot cancel selected bookings",
          text: swalText,
          confirmButtonText: "OK",
          confirmButtonColor: "#d31818",
          customClass: {
            popup: "swal-custom-popup",
            title: "swal-custom-title",
            confirmButton: "swal-custom-button",
          },
        })
      }
    }

    function init() {
      initChecks()

      try {
        document
          .getElementById("status-all")
          .addEventListener("click", function (event) {
            selectStatusFilter(event.currentTarget, true)
            manuallySetStatus = true
          })
        document
          .getElementById("status-unconfirmed")
          .addEventListener("click", function (event) {
            selectStatusFilter(event.currentTarget, true)
            manuallySetStatus = true
          })
        document
          .getElementById("status-overdue")
          .addEventListener("click", function (event) {
            selectStatusFilter(event.currentTarget, true)
            manuallySetStatus = true
          })
        document
          .getElementById("status-upcoming")
          .addEventListener("click", function (event) {
            selectStatusFilter(event.currentTarget, true)
            manuallySetStatus = true
          })
        document
          .getElementById("status-completed")
          .addEventListener("click", function (event) {
            selectStatusFilter(event.currentTarget, true)
            manuallySetStatus = true
          })
        document
          .getElementById("status-cancelled")
          .addEventListener("click", function (event) {
            selectStatusFilter(event.currentTarget, true)
            manuallySetStatus = true
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("quick-filter")
          .addEventListener("input", function () {
            applyQuickFilter()
            if (gridApi) {
              gridApi.deselectAll()
            }
            util.logAmplitudeEvent("Filter Bookings")
            if (
              !manuallySetStatus &&
              document
                .getElementById("status-unconfirmed")
                .classList.contains("selected")
            ) {
              selectStatusFilter(document.getElementById("status-all"), false)
            }
            updateQuickFilter()
          })
        document
          .getElementById("quick-filter")
          .addEventListener("keydown", function (event) {
            if (event.code === "Enter") {
              event.preventDefault()
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("export-csv")
          .addEventListener("click", function () {
            exportToCsv()
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("cancel-sessions")
          .addEventListener("click", () => {
            let selectedRows = gridApi.getSelectedRows()
            if (selectedRows.length > 0) {
              cancelSessions(selectedRows)
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("session-close").addEventListener(
          "click",
          function () {
            closeSlideOver()
            util.logAmplitudeEvent("Close Session Slide Over (Bookings)")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("edit-button").addEventListener(
          "click",
          function () {
            util.logAmplitudeEvent("Edit Session Clicked (Bookings)")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("cancel-button")
          .addEventListener("click", async function () {
            let session = clickedSession
            if (
              session.status_code != "cancelled" &&
              document
                .getElementById("cancel-button")
                .innerText.startsWith("Cancel")
            ) {
              utildashboard.cancelSession(session.session_id, isAdmin)
              util.logAmplitudeEvent("Cancelled Session (Bookings)")
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("booker-all-sessions").addEventListener(
          "click",
          function () {
            filterShowAll(clickedSession.email)
            closeSlideOver()
            util.logAmplitudeEvent("See User Bookings (Bookings)")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        if ("clipboard" in navigator) {
          document.getElementById("copy-booking-link").addEventListener(
            "click",
            async function () {
              util.logAmplitudeEvent("Copy Booking Link (Bookings)")
              let encodedEmail = encodeURIComponent(clickedSession.email)
              let url = `${window.location.origin}/bookings?filter=${encodedEmail}%20${clickedSession.booking_id}%20${clickedSession.session_id}`
              await navigator.clipboard.writeText(url)
              document.getElementById("copy-booking-link").classList.add("hide")
              document
                .getElementById("booking-link-copied")
                .classList.remove("hide")
            },
            false
          )
        } else {
          document.getElementById("copy-booking-link").classList.add("hide")
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        utildashboard.registerSpaceBookingLinkListener(
          "Copy Space Booking Link (Bookings)"
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("slide-over-container")
          .addEventListener("click", (e) => {
            if (e.target.id === "slide-over-container") {
              closeSlideOver()
              util.logAmplitudeEvent("Close Slide Overs - Click Outside")
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }
    }

    return {
      init: init,
    }
  })()
}
